import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../config/configApi";
import Swal from "sweetalert2";
import { motion } from "framer-motion";
import { BsChevronDown } from "react-icons/bs";

interface Beneficiario {
  cdPaciente: number;
  nmPaciente: string;
  nuCpf: string;
  dtNascimento: string;
  tipoBeneficiario?: string;
  matricula?: string;
  nrCarteiraPlano?: string;
  flSexo?: string;
  nuAltura?: number;
  estadoCivil?: string;
  status?: string;
  nmEndereco?: string;
  nuEndereco?: number;
  nmComplEndereco?: string;
  nmBairroEndereco?: string;
  nmCidadeEndereco?: string;
  nmUfEndereco?: string;
  cdCepEndereco?: string;
  nuTelefoneA?: string;
  nuTelefoneB?: string;
  email?: string;
  nuRg?: string;
  orgaoRg?: string;
  ufRg?: string;
  cdPlano?: number;
  dtCadPaciente?: string;
  userCadPaciente?: string;
  cdEmpresaConveniada?: string;
  cartaoSus?: string;
  pendenciaFinanceira?: boolean;
}

const AudioRecorder: React.FC = () => {
  const navigate = useNavigate();

  const [newPatient, setNewPatient] = useState({
    nome: "",
    nuCpf: "",
    cd_paciente: "",
    idade: "",
    estagio: "",
    odor: "",
    exudatopele: "",
    cor: "",
    quantidade: "",
    peleAoRedor: "",
    tamanho: "",
    fezAtendimento: false,
    naoFezAtendimento: false,
  });

  const [errors, setErrors] = useState({
    nome: false,
    nuCpf: false,
    cd_paciente: false,
    idade: false,
    estagio: false,
    odor: false,
    exudatopele: false,
    cor: false,
    quantidade: false,
    peleAoRedor: false,
    tamanho: false,
    fezAtendimento: false,
    naoFezAtendimento: false,
  });

  const [showBeneficiariosDropdown, setShowBeneficiariosDropdown] =
    useState(false);
  const [filteredBeneficiarios, setFilteredBeneficiarios] = useState<
    Beneficiario[]
  >([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Buscar pacientes conforme o texto digitado usando a API /pacientes/buscar
  useEffect(() => {
    const searchPacientes = async () => {
      if (!searchTerm || searchTerm.trim().length < 2) {
        setFilteredBeneficiarios([]);
        setShowBeneficiariosDropdown(false);
        return;
      }

      const token = localStorage.getItem("Frontend");
      if (!token) {
        console.error("Token não encontrado. Usuário precisa fazer login.");
        return;
      }

      setIsSearching(true);
      try {
        // Usando a rota /pacientes/buscar com parâmetro nome
        const response = await api.get("/sisclinic/pacientes/buscar", {
          params: {
            nome: searchTerm,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        console.log("Resposta da API de busca:", response.data);

        if (response.data && Array.isArray(response.data)) {
          setFilteredBeneficiarios(response.data.slice(0, 20)); // Limita a 20 resultados
          setShowBeneficiariosDropdown(response.data.length > 0);
        } else {
          setFilteredBeneficiarios([]);
          setShowBeneficiariosDropdown(false);
        }
      } catch (error) {
        console.error("Erro ao buscar pacientes:", error);
        setFilteredBeneficiarios([]);
        setShowBeneficiariosDropdown(false);

        // Para debug
        if (error instanceof Error) {
          console.error("Mensagem de erro:", error.message);
        }
      } finally {
        setIsSearching(false);
      }
    };

    // Limpa o timeout anterior
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Configura um novo timeout para busca (debounce)
    searchTimeoutRef.current = setTimeout(searchPacientes, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowBeneficiariosDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const calcularIdade = (dataNascimento: string): string => {
    if (!dataNascimento) return "";

    console.log("Data recebida para cálculo de idade:", dataNascimento);

    let dataNasc: Date;

    try {
      // Formato YYYY-MM-DD (da API)
      if (dataNascimento.includes("-")) {
        const partes = dataNascimento.split("-");
        if (partes.length === 3) {
          const [ano, mes, dia] = partes;
          dataNasc = new Date(Number(ano), Number(mes) - 1, Number(dia));
          console.log("Data convertida (YYYY-MM-DD):", dataNasc);
        } else {
          dataNasc = new Date(dataNascimento);
        }
      }
      // Formato DD/MM/YYYY
      else if (dataNascimento.includes("/")) {
        const partes = dataNascimento.split("/");
        if (partes.length === 3) {
          const [dia, mes, ano] = partes;
          dataNasc = new Date(Number(ano), Number(mes) - 1, Number(dia));
          console.log("Data convertida (DD/MM/YYYY):", dataNasc);
        } else {
          dataNasc = new Date(dataNascimento);
        }
      }
      // Outros formatos
      else {
        dataNasc = new Date(dataNascimento);
        console.log("Data convertida (outro formato):", dataNasc);
      }

      // Verifica se a data é válida
      if (isNaN(dataNasc.getTime())) {
        console.warn("Data de nascimento inválida:", dataNascimento);
        return "";
      }

      const hoje = new Date();
      let idade = hoje.getFullYear() - dataNasc.getFullYear();
      const mesAtual = hoje.getMonth();
      const mesNasc = dataNasc.getMonth();

      // Ajustar se ainda não fez aniversário este ano
      if (
        mesAtual < mesNasc ||
        (mesAtual === mesNasc && hoje.getDate() < dataNasc.getDate())
      ) {
        idade--;
      }

      // Verifica se a idade é válida
      if (idade < 0 || idade > 150) {
        console.warn(
          "Idade calculada inválida:",
          idade,
          "para data:",
          dataNascimento
        );
        return "";
      }

      console.log("Idade calculada:", idade, "anos");
      return idade.toString();
    } catch (error) {
      console.error("Erro ao calcular idade:", error, "Data:", dataNascimento);
      return "";
    }
  };

  const handleBeneficiarySelect = (beneficiario: Beneficiario) => {
    console.log("Beneficiário selecionado:", beneficiario);
    console.log("Nome:", beneficiario.nmPaciente);
    console.log("CPF:", beneficiario.nuCpf);
    console.log("Código do paciente:", beneficiario.cdPaciente);
    console.log("Data de Nascimento:", beneficiario.dtNascimento);

    // Calcular idade
    const idadeCalculada = calcularIdade(beneficiario.dtNascimento);
    console.log("Idade calculada:", idadeCalculada);

    // Atualizar o estado do paciente
    setNewPatient({
      nome: beneficiario.nmPaciente,
      idade: idadeCalculada,
      nuCpf: beneficiario.nuCpf || "",
      cd_paciente: beneficiario.cdPaciente
        ? beneficiario.cdPaciente.toString()
        : "",
      estagio: "",
      odor: "",
      exudatopele: "",
      cor: "",
      quantidade: "",
      peleAoRedor: "",
      tamanho: "",
      fezAtendimento: false,
      naoFezAtendimento: false,
    });

    // Atualizar o termo de busca
    setSearchTerm(beneficiario.nmPaciente);

    // Fechar dropdown
    setShowBeneficiariosDropdown(false);

    // Limpar erros
    setErrors({
      ...errors,
      nome: false,
      idade: false,
      nuCpf: false,
      cd_paciente: false,
    });
  };

  const handleNomeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Se o usuário apagar ou modificar manualmente, limpar os dados do paciente
    if (value !== newPatient.nome) {
      setNewPatient({
        ...newPatient,
        nome: value,
        cd_paciente: "",
        nuCpf: "",
        idade: "",
      });
    }

    // Mostrar dropdown se houver texto suficiente
    if (value.trim().length >= 2) {
      // A busca é feita via API no useEffect
    } else {
      setShowBeneficiariosDropdown(false);
      setFilteredBeneficiarios([]);
    }
  };

  const handleCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    checkbox: "fezAtendimento" | "naoFezAtendimento"
  ) => {
    setNewPatient((prevState) => {
      return checkbox === "fezAtendimento"
        ? {
            ...prevState,
            fezAtendimento: e.target.checked,
            naoFezAtendimento: !e.target.checked,
          }
        : {
            ...prevState,
            naoFezAtendimento: e.target.checked,
            fezAtendimento: !e.target.checked,
          };
    });
  };

  const handleSubmit = async () => {
    // Determina se os campos de avaliação são obrigatórios
    const camposAvaliacaoObrigatorios = newPatient.fezAtendimento;

    const newErrors = {
      nome: !newPatient.nome,
      nuCpf: !newPatient.nuCpf,
      cd_paciente: !newPatient.cd_paciente,
      idade: !newPatient.idade,
      estagio: camposAvaliacaoObrigatorios && !newPatient.estagio,
      odor: camposAvaliacaoObrigatorios && !newPatient.odor,
      exudatopele: camposAvaliacaoObrigatorios && !newPatient.exudatopele,
      cor: camposAvaliacaoObrigatorios && !newPatient.cor,
      quantidade: camposAvaliacaoObrigatorios && !newPatient.quantidade,
      peleAoRedor: camposAvaliacaoObrigatorios && !newPatient.peleAoRedor,
      tamanho: camposAvaliacaoObrigatorios && !newPatient.tamanho,
      fezAtendimento:
        !newPatient.fezAtendimento && !newPatient.naoFezAtendimento,
      naoFezAtendimento:
        !newPatient.fezAtendimento && !newPatient.naoFezAtendimento,
    };

    setErrors(newErrors);

    // Verifica se há algum erro
    const hasErrors = Object.values(newErrors).some((error) => error);

    if (hasErrors) {
      Swal.fire(
        "Erro!",
        "Preencha todos os campos obrigatórios antes de salvar.",
        "error"
      );
      return;
    }
    const pacienteParaSalvar = {
      nuCpf: newPatient.nuCpf ? newPatient.nuCpf : "",
      tamanho: newPatient.naoFezAtendimento ? null : newPatient.tamanho,
      estagio: newPatient.naoFezAtendimento ? null : newPatient.estagio,
      odor: newPatient.naoFezAtendimento ? null : newPatient.odor,
      exsudatoPele: newPatient.naoFezAtendimento
        ? null
        : newPatient.exudatopele, // Note a diferença no nome: exsudatoPele
      cor: newPatient.naoFezAtendimento ? null : newPatient.cor,
      quantidade: newPatient.naoFezAtendimento ? null : newPatient.quantidade,
      peleAoRedor: newPatient.naoFezAtendimento ? null : newPatient.peleAoRedor,
      fezAtendimento: newPatient.fezAtendimento,
    };

    Swal.fire({
      title: "Deseja salvar os dados?",
      text: "Os dados serão salvos no banco de dados!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sim, salvar!",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem("Frontend");
          if (!token) {
            Swal.fire({
              icon: "error",
              title: "Erro de autenticação",
              text: "Usuário não autenticado. Faça login novamente.",
            });
            return;
          }

          await api.post(
            "/sisclinic/sad/ferimentos", // Mudar a rota para /sad/ferimentos
            pacienteParaSalvar,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          Swal.fire(
            "Salvo!",
            "Os dados foram salvos com sucesso.",
            "success"
          ).then((secondResult) => {
            if (secondResult.isConfirmed) {
              Swal.fire({
                title: "Deseja ir para o prontuário?",
                icon: "question",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "Sim",
                cancelButtonText: "Cancelar",
              }).then((thirdResult) => {
                if (thirdResult.isConfirmed) {
                  setNewPatient({
                    nome: "",
                    nuCpf: "",
                    cd_paciente: "",
                    idade: "",
                    estagio: "",
                    odor: "",
                    exudatopele: "",
                    cor: "",
                    quantidade: "",
                    peleAoRedor: "",
                    tamanho: "",
                    fezAtendimento: false,
                    naoFezAtendimento: false,
                  });
                  setSearchTerm("");

                  navigate("/Record2", {
                    state: { patientData: pacienteParaSalvar },
                  });
                } else {
                  setNewPatient({
                    nome: "",
                    nuCpf: "",
                    cd_paciente: "",
                    idade: "",
                    estagio: "",
                    odor: "",
                    exudatopele: "",
                    cor: "",
                    quantidade: "",
                    peleAoRedor: "",
                    tamanho: "",
                    fezAtendimento: false,
                    naoFezAtendimento: false,
                  });
                  setSearchTerm("");

                  window.location.reload();
                }
              });
            }
          });
        } catch (error) {
          console.error("Erro ao salvar os dados:", error);
          Swal.fire("Erro!", "Ocorreu um erro ao salvar os dados.", "error");
        }
      }
    });
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
        <div className="flex justify-center items-center mb-8">
          <motion.img
            src="https://i0.wp.com/afrafepsaude.com.br/wp-content/uploads/2021/06/logo.png?w=692&ssl=1"
            alt="Logo AFRAFEP"
            className="w-60 h-auto"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          />
        </div>

        <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-white">
            <h1 className="text-2xl font-bold">
              Avaliação e Acompanhamento das Feridas
            </h1>
            <p className="text-blue-100">Registro de avaliação de feridas</p>
          </div>

          <div className="p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-800">
                Dados do Paciente
              </h2>
              <button
                onClick={() => navigate(-1)}
                className="flex items-center px-4 py-2 bg-white border border-red-500 text-red-500 rounded-lg hover:bg-red-50 transition-colors duration-200"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
                Voltar
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="relative" ref={dropdownRef}>
                <label
                  htmlFor="nome"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Nome do Beneficiário *
                </label>
                <div className="relative">
                  <div className="flex items-center">
                    <input
                      id="nome"
                      type="text"
                      value={searchTerm}
                      onChange={handleNomeChange}
                      onFocus={() => {
                        if (
                          searchTerm.trim().length >= 2 &&
                          filteredBeneficiarios.length > 0
                        ) {
                          setShowBeneficiariosDropdown(true);
                        }
                      }}
                      className={`w-full px-4 py-2.5 rounded-lg border ${
                        errors.nome ? "border-red-500" : "border-gray-300"
                      } focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 pr-10`}
                      placeholder="Digite o nome do paciente (mínimo 2 caracteres)"
                      autoComplete="off"
                    />
                    <BsChevronDown
                      className="absolute right-3 text-gray-400 cursor-pointer"
                      onClick={() => {
                        if (
                          searchTerm.trim().length >= 2 &&
                          filteredBeneficiarios.length > 0
                        ) {
                          setShowBeneficiariosDropdown(
                            !showBeneficiariosDropdown
                          );
                        }
                      }}
                    />
                  </div>

                  {showBeneficiariosDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {isSearching ? (
                        <div className="px-4 py-3 text-gray-500 text-center">
                          <div className="inline-block animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-500 mr-2"></div>
                          Buscando pacientes...
                        </div>
                      ) : filteredBeneficiarios.length > 0 ? (
                        filteredBeneficiarios.map((beneficiario) => (
                          <div
                            key={`${beneficiario.nmPaciente}-${beneficiario.cdPaciente}`}
                            className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                            onClick={() =>
                              handleBeneficiarySelect(beneficiario)
                            }
                          >
                            <div className="font-medium">
                              {beneficiario.nmPaciente}
                            </div>
                            <div className="text-sm text-gray-500 flex flex-wrap gap-2">
                              {beneficiario.nuCpf && (
                                <span>CPF: {beneficiario.nuCpf}</span>
                              )}
                              {beneficiario.dtNascimento && (
                                <span>Nasc: {beneficiario.dtNascimento}</span>
                              )}
                              {beneficiario.matricula && (
                                <span>Matrícula: {beneficiario.matricula}</span>
                              )}
                            </div>
                          </div>
                        ))
                      ) : searchTerm.trim().length >= 2 ? (
                        <div className="px-4 py-3 text-gray-500">
                          Nenhum paciente encontrado para "{searchTerm}"
                        </div>
                      ) : (
                        <div className="px-4 py-3 text-gray-500">
                          Digite pelo menos 2 caracteres para buscar
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {errors.nome && (
                  <p className="mt-1 text-sm text-red-600">
                    Nome é obrigatório.
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="idade"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Idade *
                </label>
                <input
                  id="idade"
                  type="text"
                  name="idade"
                  value={newPatient.idade}
                  readOnly
                  className={`w-full px-4 py-2.5 rounded-lg border ${
                    errors.idade ? "border-red-500" : "border-gray-300"
                  } bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200`}
                />
                {errors.idade && (
                  <p className="mt-1 text-sm text-red-600">
                    Idade é obrigatória.
                  </p>
                )}
              </div>
            </div>

            {!newPatient.naoFezAtendimento && (
              <div className="mb-6">
                <div className="flex items-center mb-4">
                  <div className="h-0.5 w-8 bg-blue-600 mr-3"></div>
                  <h3 className="text-lg font-semibold text-gray-800">
                    Avaliação da Ferida
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Estágio *
                    </label>
                    <div className="space-y-3">
                      {[
                        { value: "Avermelhada", label: "I - Avermelhada" },
                        {
                          value:
                            "Bolhas/lesão pequena espessura (epiderme/derme)",
                          label:
                            "II - Bolhas/lesão pequena espessura (epiderme/derme)",
                        },
                        {
                          value: "Lesão total de pele abrangendo subcutâneo",
                          label:
                            "III - Lesão total de pele abrangendo subcutâneo",
                        },
                        {
                          value:
                            "Lesão total de pele envolvendo músculos, tendões e/ou ossos",
                          label:
                            "IV - Lesão total de pele envolvendo músculos, tendões e/ou ossos",
                        },
                      ].map((option) => (
                        <label
                          key={option.value}
                          className="flex items-start p-2 rounded-md hover:bg-blue-100 transition-colors duration-200"
                        >
                          <input
                            type="radio"
                            name="estagio"
                            value={option.value}
                            checked={newPatient.estagio === option.value}
                            onChange={(e) =>
                              setNewPatient({
                                ...newPatient,
                                estagio: e.target.value,
                              })
                            }
                            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <span className="ml-3 text-sm text-gray-700">
                            {option.label}
                          </span>
                        </label>
                      ))}
                    </div>
                    {errors.estagio && (
                      <p className="mt-2 text-sm text-red-600">
                        Estágio é obrigatório.
                      </p>
                    )}
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Cor *
                    </label>
                    <div className="space-y-3">
                      {[
                        { value: "rosea", label: "Rósea" },
                        { value: "amarela", label: "Amarela" },                        
                        {
                          value: "cinza",
                          label: "Cinza",
                        },
                          {
                          value: "preto",
                          label: "Preto",
                        },
                          {
                          value: "necrose",
                          label: "Necrose",
                        },
                      ].map((option) => (
                        <label
                          key={option.value}
                          className="flex items-start p-2 rounded-md hover:bg-blue-100 transition-colors duration-200"
                        >
                          <input
                            type="radio"
                            name="cor"
                            value={option.value}
                            checked={newPatient.cor === option.value}
                            onChange={(e) =>
                              setNewPatient({
                                ...newPatient,
                                cor: e.target.value,
                              })
                            }
                            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <span className="ml-3 text-sm text-gray-700">
                            {option.label}
                          </span>
                        </label>
                      ))}
                    </div>
                    {errors.cor && (
                      <p className="mt-2 text-sm text-red-600">
                        Cor é obrigatória.
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Odor *
                    </label>
                    <div className="space-y-3">
                      {[
                        { value: "discreto", label: "Discreto" },
                        { value: "moderado", label: "Moderado" },
                        { value: "forte", label: "Forte" },
                      ].map((option) => (
                        <label
                          key={option.value}
                          className="flex items-start p-2 rounded-md hover:bg-blue-100 transition-colors duration-200"
                        >
                          <input
                            type="radio"
                            name="odor"
                            value={option.value}
                            checked={newPatient.odor === option.value}
                            onChange={(e) =>
                              setNewPatient({
                                ...newPatient,
                                odor: e.target.value,
                              })
                            }
                            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <span className="ml-3 text-sm text-gray-700">
                            {option.label}
                          </span>
                        </label>
                      ))}
                    </div>
                    {errors.odor && (
                      <p className="mt-2 text-sm text-red-600">
                        Odor é obrigatório.
                      </p>
                    )}
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Exudato/Pele *
                    </label>
                    <div className="space-y-3">
                      {[
                        { value: "seroso", label: "Seroso" },
                        {
                          value: "serosanguinolento",
                          label: "Serosanguinolento",
                        },
                        { value: "purulento", label: "Purulento" },
                      ].map((option) => (
                        <label
                          key={option.value}
                          className="flex items-start p-2 rounded-md hover:bg-blue-100 transition-colors duration-200"
                        >
                          <input
                            type="radio"
                            name="exudatopele"
                            value={option.value}
                            checked={newPatient.exudatopele === option.value}
                            onChange={(e) =>
                              setNewPatient({
                                ...newPatient,
                                exudatopele: e.target.value,
                              })
                            }
                            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <span className="ml-3 text-sm text-gray-700">
                            {option.label}
                          </span>
                        </label>
                      ))}
                    </div>
                    {errors.exudatopele && (
                      <p className="mt-2 text-sm text-red-600">
                        Selecione uma opção.
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Quantidade *
                    </label>
                    <div className="space-y-3">
                      {[
                        { value: "nenhum", label: "Nenhum" },
                        { value: "pouco", label: "Pouco" },
                        { value: "media", label: "Média" },
                        { value: "grande", label: "Grande" },
                      ].map((option) => (
                        <label
                          key={option.value}
                          className="flex items-start p-2 rounded-md hover:bg-blue-100 transition-colors duration-200"
                        >
                          <input
                            type="radio"
                            name="quantidade"
                            value={option.value}
                            checked={newPatient.quantidade === option.value}
                            onChange={(e) =>
                              setNewPatient({
                                ...newPatient,
                                quantidade: e.target.value,
                              })
                            }
                            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <span className="ml-3 text-sm text-gray-700">
                            {option.label}
                          </span>
                        </label>
                      ))}
                    </div>
                    {errors.quantidade && (
                      <p className="mt-2 text-sm text-red-600">
                        Selecione uma opção.
                      </p>
                    )}
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Pele ao redor *
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { value: "normal", label: "Normal" },
                        { value: "vermelha", label: "Vermelha" },
                        { value: "induracao", label: "Induração" },
                        { value: "mac", label: "Mac" },
                        { value: "edema", label: "Edema" },
                        { value: "hematoma", label: "Hematoma" },
                        { value: "cianose", label: "Cianose" },
                        { value: "bolha", label: "Bolha" },
                        { value: "frio", label: "Frio" },
                      ].map((option) => (
                        <label
                          key={option.value}
                          className="flex items-start p-2 rounded-md hover:bg-blue-100 transition-colors duration-200"
                        >
                          <input
                            type="radio"
                            name="peleAoRedor"
                            value={option.value}
                            checked={newPatient.peleAoRedor === option.value}
                            onChange={(e) =>
                              setNewPatient({
                                ...newPatient,
                                peleAoRedor: e.target.value,
                              })
                            }
                            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            {option.label}
                          </span>
                        </label>
                      ))}
                    </div>
                    {errors.peleAoRedor && (
                      <p className="mt-2 text-sm text-red-600">
                        Selecione uma opção.
                      </p>
                    )}
                  </div>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <label
                    htmlFor="tamanho"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    TAMANHO (CM) *
                  </label>
                  <input
                    id="tamanho"
                    type="text"
                    name="tamanho"
                    value={newPatient.tamanho}
                    onChange={(e) =>
                      setNewPatient({
                        ...newPatient,
                        tamanho: e.target.value,
                      })
                    }
                    className={`w-full px-4 py-2.5 rounded-lg border ${
                      errors.tamanho ? "border-red-500" : "border-gray-300"
                    } focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200`}
                    placeholder="Digite o tamanho em centímetros"
                  />
                  {errors.tamanho && (
                    <p className="mt-2 text-sm text-red-600">
                      Tamanho é obrigatório.
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="mb-8">
              <div className="flex items-center mb-4">
                <div className="h-0.5 w-8 bg-blue-600 mr-3"></div>
                <h3 className="text-lg font-semibold text-gray-800">
                  Status do Atendimento
                </h3>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <label
                  htmlFor="fezAtendimento"
                  className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-blue-50 transition-colors duration-200 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    id="fezAtendimento"
                    checked={newPatient.fezAtendimento}
                    onChange={(e) => handleCheckboxChange(e, "fezAtendimento")}
                    className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-3 text-gray-700 font-medium">
                    Fez o atendimento
                  </span>
                </label>

                <label
                  htmlFor="naoFezAtendimento"
                  className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-blue-50 transition-colors duration-200 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    id="naoFezAtendimento"
                    checked={newPatient.naoFezAtendimento}
                    onChange={(e) =>
                      handleCheckboxChange(e, "naoFezAtendimento")
                    }
                    className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-3 text-gray-700 font-medium">
                    Não fez o atendimento
                  </span>
                </label>
              </div>

              {errors.fezAtendimento && (
                <p className="mt-2 text-sm text-red-600">
                  É obrigatório selecionar uma opção de atendimento.
                </p>
              )}
            </div>

            <div className="flex justify-center mt-8">
              <motion.button
                onClick={handleSubmit}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-700 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-300 flex items-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Salvar
              </motion.button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AudioRecorder;
